import React from "react";

import _ from "lodash";
import { Route, Switch, useLocation, useHistory } from "react-router-dom";

import ROUTES from "manager/config/routes";
import NotificationCounter from "shared/components/NotificationCounter";
import Tabs from "shared/components/Tabs";
import Tile from "shared/components/Tile";
import { useDeviceType } from "shared/hooks";
import { useVeroFlag } from "shared/utils/featureFlags";
import { transformRoute } from "shared/utils/routing";
import { VeroFlag } from "shared/utils/veroFlags";

export const AppTabs: React.FC<{
  dealId: string;
  manualNotes?: number;
  Activity: React.FC<{ dealId: string }>;
  /** Absent on VERO1 deals — the deal review tab is the run surface, and
   *  leasing must not reach raw runs (D13). */
  Lumen?: React.FC;
  Ledger: React.FC;
  /** Deal review tab (PRODUCT-2060) — passed only on VERO1-active deals.
   *  An element (not a component): a fresh component identity per parent
   *  render would remount the tab and reset its picker/filter state. */
  DealReview?: React.ReactNode;
}> = ({
  dealId,
  manualNotes,
  Activity,
  Lumen,
  Ledger,
  DealReview,
  children,
}) => {
  const history = useHistory();
  const { isMobile } = useDeviceType();
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign);
  const dealRoute = transformRoute(ROUTES.deal, { id: dealId });
  const dealActivityRoute = transformRoute(ROUTES.dealActivity, { id: dealId });
  const dealLumenRoute = transformRoute(ROUTES.dealLumen, { id: dealId });
  const dealReviewRoute = transformRoute(ROUTES.dealReview, { id: dealId });
  const dealLedgerRoute = transformRoute(ROUTES.dealLedger, { id: dealId });
  const activeRoute = useLocation().pathname;
  const tabs = _.compact([
    { key: dealRoute, label: "Applicants" },
    {
      key: dealActivityRoute,
      label: (
        <>
          <span style={{ marginRight: 5 }}>Activity</span>
          {!!manualNotes && <NotificationCounter count={manualNotes} />}
        </>
      ),
    },
    Lumen && {
      key: dealLumenRoute,
      label: useDealsListUiRedesign ? "Checklist" : "Lumen",
    },
    DealReview && { key: dealReviewRoute, label: "Deal review" },
    { key: dealLedgerRoute, label: "Ledger" },
  ]);

  return (
    <>
      <Tile>
        <Tabs
          activeKey={activeRoute}
          tabs={tabs}
          onChange={(newRoute: string) => {
            history.push(newRoute);
          }}
          alignFirstTabTo18px={useDealsListUiRedesign && !isMobile}
        />
      </Tile>

      <Switch>
        <Route exact path={dealRoute} render={() => children} />
        <Route
          exact
          path={dealActivityRoute}
          render={() => <Activity dealId={dealId} />}
        />
        {Lumen && (
          <Route exact path={dealLumenRoute} render={() => <Lumen />} />
        )}
        {DealReview && (
          <Route
            exact
            path={dealReviewRoute}
            render={() => <>{DealReview}</>}
          />
        )}
        <Route exact path={dealLedgerRoute} render={() => <Ledger />} />
      </Switch>
    </>
  );
};
